import "navigator.locks";
import StorageService from "@/services/storage.ts";
import {uuidv7} from "uuidv7";
import type {ApiError} from "./api";
import ApiService from "./api";
import JwtService from "./jwt";

export const LogSayfa = {
  LOGGER: 1,
  API: 2,
  GIRIS: 10,
  ANASAYFA: 20,
  YONERGE: 30,
  YARISMA: 40,
  YARISMACI_ANALIZ: 60,
  SONUC: 70,
} as const;

export type LogSayfaType = (typeof LogSayfa)[keyof typeof LogSayfa];

export const LogIslem = {
  HATA: 1,
  SYNC: 2,
  // login
  LOGIN_KOD: 10,
  LOGIN_TELEFON: 11,
  LOGIN_EMAIL: 12,
  // anasayfa
  ANASAYFA_GORUNTULEME: 20,
  ANASAYFA_SORU_PRELOAD: 21,
  ANASAYFA_INTERNET_YAVAS: 22,
  // yonerge
  YONERGE_GORUNTULEME: 30,
  YONERGE_ONAY: 31,
  YONERGE_REDIRECT: 32,
  // yarisma - giriş
  YARISMA_GORUNTULEME: 40,
  YARISMA_BASLADI: 41,
  // yarisma - soru akışı
  YARISMA_SORU_GORUNTULEME: 42,
  YARISMA_CEVAP: 43,
  YARISMA_PREV: 44,
  YARISMA_NEXT: 45,
  YARISMA_INDEXTEN_SORU_DEGISTIRME: 46,
  // yarisma - bitirme
  YARISMA_BITIRE_BASDI: 47,
  YARISMA_BITIR_VAZGECTI: 48,
  YARISMA_BITIR_ONAY: 49,
  YARISMA_SURE_BITTI: 50,
  YARISMA_BITIS_SAYFASINA_GIT: 51,
  // yarisma - çıkış
  YARISMA_CIKISA_BASTI: 52,
  YARISMA_CIKIS_VAZGECTI: 53,
  YARISMA_CIKIS_ONAY: 54,
  // yarisma - exception
  YARISMA_BEFORE_UNLOAD: 55,
  // yarismaci analiz
  YARISMACI_DEVICE_INFO: 60,
  YARISMACI_EXTENSION_IFRAME: 61,
  YARISMACI_EXTENSION_CANVAS: 62,
  YARISMACI_EXTENSION_KEYWORD: 63,
  // sonuc
  SONUC_GORUNTULEME: 70,
} as const;

export type LogIslemType = (typeof LogIslem)[keyof typeof LogIslem];

type BehavioralMetrics = {
  mc: number;   // Mouse movement count
  md: number;   // Mouse distance (px)
  tc: number;   // Touch count
  td: number;   // Touch distance (px)
  acd: number;  // Avg click delay (ms)
  msp: number;  // Mouse speed (px/s)
  mit: number;  // Mouse idle time (ms)
  me: number;   // Movement efficiency (md/mc)
};

type LogEntry = {
  lid: number;
  si: number;
  ct: number;
  s: LogSayfaType;
  i: LogIslemType;
  d?: unknown;
  bm?: BehavioralMetrics;  // Behavioral metrics for bot detection
};

type NavigatorLocks = {
  request: (name: string, callback: (lock: unknown) => Promise<void>) => Promise<void>;
};

const AUDIT_LOG_KEY_PREFIX = "al:";
const AUDIT_TOKEN_KEY_PREFIX = "alt:";
const AUDIT_LAST_SYNC_KEY = "al-ls";
const AUDIT_RETRY_KEY = "al-r";
const AUDIT_NEXT_RETRY_KEY = "al-nr";
const AUDIT_CLIENT_ID_KEY = "al-cid";
const AUDIT_SESSION_ID_KEY = "al-sid";
const AUDIT_SECURITY_LOGGED_KEY = "al-sl";
const MAX_RETRY_COUNT = 8; // Maksimum retry sayısı (8 deneme = ~40 dakika bekleme)

class LogService {
  static log(sayfa: LogSayfaType, islem: LogIslemType, data?: Record<string, unknown>, behavioralMetrics?: BehavioralMetrics): void {
    const yaid = JwtService.getTokenObj().yaid;
    if (!yaid) {
      console.warn("[LogService] No yaid found");
      return;
    }

    const session_id = LogService.getSessionId();
    const logId = customId();

    const log: LogEntry = {
      lid: logId,
      si: session_id,
      ct: Date.now(),
      s: sayfa,
      i: islem,
      d: data,
      bm: behavioralMetrics,
    };
    StorageService.set(`${AUDIT_LOG_KEY_PREFIX}${yaid}:${logId}`, log);
    StorageService.set(AUDIT_TOKEN_KEY_PREFIX + yaid, JwtService.getAccessToken());

    let count = 0;
    for (let i = 0, len = localStorage.length; i < len; ++i) {
      const key = localStorage.key(i);
      if (key?.startsWith(AUDIT_LOG_KEY_PREFIX)) {
        count++;
      }
    }

    if (count > 10) {
      void LogService.sync();
    }
  }

  static async sync(): Promise<void> {
    const lastSyncValue = StorageService.get(AUDIT_LAST_SYNC_KEY);
    const lastSync = typeof lastSyncValue === "number" ? lastSyncValue : null;
    if (lastSync && Date.now() - lastSync < 5 * 1000) {
      return;
    }

    // Retry zamanı kontrolü (exponential backoff için)
    const nextRetryValue = StorageService.get(AUDIT_NEXT_RETRY_KEY);
    const nextRetry = typeof nextRetryValue === "number" ? nextRetryValue : 0;
    if (nextRetry > Date.now()) {
      return; // Henüz retry zamanı gelmemiş
    }

    const locks = (navigator as Navigator & { locks: NavigatorLocks }).locks;
    await locks.request("logger_sync", async () => {
      const invalidKeys: string[] = [];
      const logsByYaid = new Map<string, { keys: string[], logs: LogEntry[] }>();

      // Logları topla ve yaid'ye göre grupla (key'den parse et)
      for (let i = 0, len = localStorage.length; i < len; ++i) {
        const key = localStorage.key(i);
        if (!key?.startsWith(AUDIT_LOG_KEY_PREFIX)) {
          continue;
        }
        const log = StorageService.get(key) as LogEntry | null;
        if (!log) {
          invalidKeys.push(key);
          continue;
        }

        // key format: log:yaid:logid
        const yaid = key.split(":")[1] || "0";
        if (!logsByYaid.has(yaid)) {
          logsByYaid.set(yaid, {keys: [], logs: []});
        }
        const group = logsByYaid.get(yaid)!;
        group.keys.push(key);
        group.logs.push(log);
      }

      if (logsByYaid.size === 0) {
        invalidKeys.forEach(StorageService.delete);
        return;
      }

      StorageService.set(AUDIT_LAST_SYNC_KEY, Date.now());

      // Her yaid grubu için ayrı gönder
      let hasError = false;
      for (const [yaid, {keys, logs}] of logsByYaid) {
        const err = await LogService.sendWithFetch(yaid, logs);
        if (err) {
          hasError = true;
          const retryValue = StorageService.get(AUDIT_RETRY_KEY);
          const retry = typeof retryValue === "number" ? retryValue : 0;

          // Maksimum retry sayısına ulaşıldıysa logları sil ve vazgeç
          if (retry >= MAX_RETRY_COUNT) {
            console.warn(`[LogService] Max retry count (${MAX_RETRY_COUNT}) reached. Dropping ${logs.length} logs.`, {
              yaid,
              finalError: err,
              logCount: logs.length
            });

            // Logları sil
            keys.forEach(StorageService.delete);

            // Retry metadata'yı sıfırla
            StorageService.set(AUDIT_RETRY_KEY, 0);
            StorageService.set(AUDIT_NEXT_RETRY_KEY, 0);

            continue;
          }

          // Exponential backoff: 5s, 10s, 20s, 40s, 80s...
          const backoffSeconds = 5 * Math.pow(2, retry);
          const nextRetryAt = Date.now() + backoffSeconds * 1000;

          StorageService.set(AUDIT_RETRY_KEY, retry + 1);
          StorageService.set(AUDIT_NEXT_RETRY_KEY, nextRetryAt);

          // İlk 3 denemede hata logu ekle
          if (retry < 3) {
            LogService.log(LogSayfa.LOGGER, LogIslem.SYNC, {
              err: `${err}`,
              retryCount: retry + 1,
              nextRetryAt,
              logCount: logs.length
            });
          }
          continue; // Bu grubun loglarını silme
        }

        // Başarılı! Bu grubun loglarını sil
        keys.forEach(StorageService.delete);
      }

      // Tüm gruplar başarılıysa retry metadata'yı sıfırla
      if (!hasError) {
        StorageService.set(AUDIT_RETRY_KEY, 0);
        StorageService.set(AUDIT_NEXT_RETRY_KEY, 0);
      }

      invalidKeys.forEach(StorageService.delete);
    });
  }

  static async syncForce(): Promise<void> {
    const logsByYaid = new Map<string, { keys: string[], logs: LogEntry[] }>();

    // Tüm logları topla ve yaid'ye göre grupla
    for (let i = 0, len = localStorage.length; i < len; ++i) {
      const key = localStorage.key(i);
      if (!key?.startsWith(AUDIT_LOG_KEY_PREFIX)) {
        continue;
      }
      const log = StorageService.get(key) as LogEntry | null;
      if (!log) {
        continue;
      }

      // key format: al:yaid:logid
      const yaid = key.split(":")[1] || "0";
      if (!logsByYaid.has(yaid)) {
        logsByYaid.set(yaid, {keys: [], logs: []});
      }
      const group = logsByYaid.get(yaid)!;
      group.keys.push(key);
      group.logs.push(log);
    }

    if (logsByYaid.size === 0) {
      return;
    }

    // Her yaid grubu için ayrı gönder
    for (const [yaid, {keys, logs}] of logsByYaid) {
      // sendBeacon ile gönder (best effort)
      let sent = LogService.sendWithBeacon(yaid, logs);
      // fallback to fetch
      if (!sent) {
        const err = await LogService.sendWithFetch(yaid, logs, true);
        if (!err) {
          sent = true;
        }
      }
      // Başarılıysa bu grubun loglarını sil
      if (sent) {
        keys.forEach(StorageService.delete);
      }
    }

    // Tüm gruplar gönderildiyse retry metadata'yı sıfırla
    StorageService.set(AUDIT_RETRY_KEY, 0);
    StorageService.set(AUDIT_NEXT_RETRY_KEY, 0);
  }

  /**
   * Normal fetch ile log gönderimi (response kontrolü ile)
   */
  private static async sendWithFetch(yaid: string, logs: LogEntry[], force: boolean = false): Promise<ApiError> {
    const endpoint = ApiService.baseUrl + `log`;
    const token = StorageService.get(AUDIT_TOKEN_KEY_PREFIX + yaid) as string | null;
    const ci = LogService.getClientId();
    const payload = {t: token, ci: ci, l: logs};

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        keepalive: force,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        return `HTTP ${response.status}`;
      }

      return null;
    } catch (err) {
      return `${err}`;
    }
  }

  /**
   * sendBeacon ile son şans gönderimi (beforeunload için)
   * Response alamayız ama daha garantili
   */
  private static sendWithBeacon(yaid: string, logs: LogEntry[]): boolean {
    const endpoint = ApiService.baseUrl + `log`;
    const token = StorageService.get(AUDIT_TOKEN_KEY_PREFIX + yaid) as string | null;
    const ci = LogService.getClientId();
    const payload = JSON.stringify({t: token, ci: ci, l: logs});
    const blob = new Blob([payload], {type: "application/json"});

    if (navigator.sendBeacon) {
      return navigator.sendBeacon(endpoint, blob);
    }

    return false;
  }

  /**
   * Sayfa kapatılırken logları güvenli şekilde gönder
   */
  static setupUnloadHandler(): void {
    // beforeunload: Sayfa kapatılıyor/refresh
    window.addEventListener("beforeunload", () => {
      LogService.log(LogSayfa.YARISMA, LogIslem.YARISMA_BEFORE_UNLOAD);
      void LogService.syncForce();
    });

    // pagehide: beforeunload'dan daha güvenilir (mobil için)
    window.addEventListener("pagehide", () => {
      void LogService.syncForce();
    });

    // visibilitychange: Tab gizlendiğinde
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        // Tab backgrounda gitti, hemen sync yap
        void LogService.syncForce();
      }
    });
  }

  /**
   * Security Fingerprinting for Bot Detection
   * ==========================================
   *
   * ACTIVE METRICS:
   * ---------------
   * ua     - User Agent           : Browser/OS bilgisi, analiz için kritik
   * wd     - WebDriver flag       : Selenium/Puppeteer temel tespiti
   * canvas - Canvas fingerprint   : Farklı browser = farklı hash (Atlas != Chrome!)
   * screen - Ekran boyutu         : Headless genelde 800x600, gerçek kullanıcı farklı
   * lang   - Dil ayarı            : Fingerprint, tutarsızlık tespiti
   * tz     - Timezone offset      : Fingerprint, lokasyon tutarsızlığı
   * touch  - Touch desteği        : Mobile detection, behavioral ile eşleştirme
   * hw     - Hardware concurrency : CPU core sayısı, VM genelde 1-2
   * mem    - Device memory (GB)   : VM/container genelde düşük
   * plugins - Plugin sayısı       : Headless = 0, gerçek browser > 0
   * webgl  - WebGL renderer       : VM detection (SwiftShader = headless)
   *
   * REMOVED METRICS (2024-12 analysis - all bypassed by Atlas):
   * -----------------------------------------------------------
   * phantom   - PhantomJS detection    : Her zaman false, modern botlar bypass
   * nightmare - Nightmare.js detection : Her zaman false, kullanılmıyor
   * domAuto   - DOM automation         : Her zaman false, bypass edildi
   * selenium  - Selenium detection     : Her zaman false, bypass edildi
   * cdcLeak   - ChromeDriver leak      : Her zaman false, bypass edildi
   * cdp       - CDP detection          : Her zaman false, Atlas bypass ediyor
   * aiExt     - AI extension detection : Hiç tetiklenmedi, selector'lar etkisiz
   */
  static logSecurityLogs() {
    const yaid = JwtService.getTokenObj().yaid;
    if (!yaid) return;

    const securityLogged = StorageService.get<boolean>(AUDIT_SECURITY_LOGGED_KEY + ":" + yaid);
    if (securityLogged === true) {
      return;
    }
    StorageService.set(AUDIT_SECURITY_LOGGED_KEY + ":" + yaid, true);

    // Canvas Fingerprint - Atlas'ta farklı değer üretiyor!
    let canvas = '';
    try {
      const c = document.createElement('canvas');
      const ctx = c.getContext('2d');
      if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('YO-SEC-2025', 2, 2);
        canvas = c.toDataURL().slice(-50);
      }
    } catch (_) { /* ignore */
    }

    // WebGL Renderer - VM/Headless detection
    let webgl = '';
    try {
      const c = document.createElement('canvas');
      const gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      if (gl) {
        const ext = (gl as WebGLRenderingContext).getExtension('WEBGL_debug_renderer_info');
        if (ext) {
          webgl = (gl as WebGLRenderingContext).getParameter(ext.UNMASKED_RENDERER_WEBGL) || '';
        }
      }
    } catch (_) { /* ignore */
    }

    LogService.log(LogSayfa.YARISMACI_ANALIZ, LogIslem.YARISMACI_DEVICE_INFO, {
      wd: !!navigator.webdriver,
      canvas,
      screen: `${screen.width}x${screen.height}`,
      lang: navigator.language,
      tz: new Date().getTimezoneOffset(),
      touch: navigator.maxTouchPoints > 0,
      hw: navigator.hardwareConcurrency || 0,
      mem: (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0,
      plugins: navigator.plugins?.length || 0,
      webgl: webgl.slice(0, 100),
    });
  }

  static getClientId(): string {
    let client_id = StorageService.get(AUDIT_CLIENT_ID_KEY) as string | null;
    if (typeof client_id !== "string" || client_id.length === 0) {
      client_id = uuidv7();
      StorageService.set(AUDIT_CLIENT_ID_KEY, client_id);
    }
    return client_id;
  }

  static getSessionId(): number {
    let session_id = window.sessionStorage.getItem(AUDIT_SESSION_ID_KEY)
    if (!session_id) {
      session_id = customId().toString()
      window.sessionStorage.setItem(AUDIT_SESSION_ID_KEY, session_id)
    }
    return parseInt(session_id)
  }
}

export default LogService;

export function customId(): number {
  const randomBuffer = new Uint32Array(1);
  window.crypto.getRandomValues(randomBuffer);

  const timestampSeconds = Math.floor(Date.now() / 1000) >>> 0; // 32 bit
  const randomBits = randomBuffer[0]! & 0x1FFFFF; // Son 21 bit (0x1FFFFF = 2^21 - 1 = 2097151)

  // İlk 32 bit: timestamp, Son 21 bit: rasgele
  // 2^21 = 2097152 ile çarparak 21 bit sola kaydırma
  return timestampSeconds * 2097152 + randomBits;
}
